import { computed, watch, type Ref } from "vue";
import { useQueryClient } from "@tanstack/vue-query";
import {
  useSubscription,
  ChannelsEnum,
} from "@/shared/composables/useSubscription";
import { useImportsStore } from "@/admin/stores/imports";
import { useAppNotifications } from "@/shared/composables/useAppNotifications";
import { useI18n } from "@/shared/composables/useI18n";
import {
  type Import,
  ImportStatusEnum,
  useImports,
} from "@/services/fyAdminApi";

const formatImportLabel = (importData: Import): string => {
  return importData.type
    .replace("Imports::", "")
    .replace("::", " ")
    .replace(/([A-Z])/g, " $1")
    .replace(/^ /, "")
    .trim();
};

export const useImportUpdates = (enabled: Ref<boolean>) => {
  const importsStore = useImportsStore();
  const { displayInfo, displaySuccess, displayAlert } = useAppNotifications();
  const { t } = useI18n();
  const queryClient = useQueryClient();

  const handleImportUpdate = (data: string) => {
    const importData: Import = JSON.parse(data);

    const previousImport = importsStore.imports[importData.id];
    const previousStatus = previousImport?.status;

    importsStore.upsertImport(importData);

    if (importData.status === previousStatus) {
      return;
    }

    void queryClient.invalidateQueries({ queryKey: ["imports"] });

    const label = formatImportLabel(importData);

    switch (importData.status) {
      case ImportStatusEnum.STARTED:
        displayInfo({
          text: t("messages.import.started", { type: label }),
        });
        break;
      case ImportStatusEnum.FINISHED:
        displaySuccess({
          text: t("messages.import.finished", { type: label }),
        });
        break;
      case ImportStatusEnum.FAILED:
        displayAlert({
          text: t("messages.import.failed", {
            type: label,
            info: importData.info || "",
          }),
        });
        break;
    }
  };

  useSubscription({
    channelName: ChannelsEnum.IMPORTS,
    received: handleImportUpdate,
  });

  // Seed store with currently active imports on page load
  const activeImportsParams = computed(() => ({
    perPage: "50",
  }));

  const { data: existingImports } = useImports(activeImportsParams, {
    query: {
      enabled,
      staleTime: 30_000,
    },
  });

  watch(
    () => existingImports.value,
    (data) => {
      if (data?.items) {
        const active = data.items.filter(
          (imp) =>
            imp.status === ImportStatusEnum.CREATED ||
            imp.status === ImportStatusEnum.STARTED,
        );
        importsStore.seedImports(active);
      }
    },
    { immediate: true },
  );
};
